import React, { useState, useEffect, useRef, useCallback } from 'react';
// @twilio/voice-sdk is dynamically imported inside the init effect —
// keeps the ~150kB SDK out of the main bundle since most users never
// trigger a call. The original `import { Device } from '@twilio/voice-sdk'`
// lived here.
import { API_BASE_URL } from '../config/api';

const API = API_BASE_URL;

/**
 * CallWidget — in-app phone calling via Twilio Voice SDK v2.
 * Uses @twilio/voice-sdk npm package.
 */
const CallWidget = ({ candidateId, candidateName, phoneNumber, onClose, onCallComplete }) => {
  const [status, setStatus] = useState('initializing');
  const [activeCall, setActiveCall] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [callLogId, setCallLogId] = useState(null);
  const [callerId, setCallerId] = useState(null);
  const [dialNumber, setDialNumber] = useState(phoneNumber || '');
  const [callNotes, setCallNotes] = useState('');
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState(null);
  const [savingNotes, setSavingNotes] = useState(false);
  const [recording, setRecording] = useState(true);

  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const deviceRef = useRef(null);

  const authToken = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const tokenRes = await fetch(`${API}/api/voice/token`, { headers });
        if (!tokenRes.ok) throw new Error('Failed to get voice token');
        const tokenData = await tokenRes.json();

        if (cancelled) return;
        setCallerId(tokenData.caller_id);

        if (!tokenData.caller_id) {
          setError('No phone number assigned. Go to Profile → Phone & SMS to get one.');
          setStatus('error');
          return;
        }

        // Lazy-load the voice SDK on first use. Cached after first import.
        const { Device } = await import('@twilio/voice-sdk');
        if (cancelled) return;

        const device = new Device(tokenData.token, {
          codecPreferences: ['opus', 'pcmu'],
          logLevel: 'warn',
        });

        device.on('registered', () => {
          if (!cancelled) setStatus('ready');
        });

        device.on('error', (err) => {
          console.error('[Voice] Device error:', err);
          if (!cancelled) setError(err.message || 'Connection error');
        });

        device.on('tokenWillExpire', async () => {
          try {
            const res = await fetch(`${API}/api/voice/token`, { headers });
            if (res.ok) { const d = await res.json(); device.updateToken(d.token); }
          } catch (e) {}
        });

        await device.register();
        deviceRef.current = device;

      } catch (err) {
        if (!cancelled) {
          console.error('[Voice] Init error:', err);
          setError(err.message);
          setStatus('error');
        }
      }
    };

    init();

    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
      if (deviceRef.current) {
        try { deviceRef.current.disconnectAll(); deviceRef.current.unregister(); deviceRef.current.destroy(); } catch (e) {}
      }
    };
  }, []);

  const makeCall = useCallback(async () => {
    if (!deviceRef.current || !dialNumber) return;
    try {
      setStatus('calling');
      setError(null);

      let logId = '';
      const logRes = await fetch(`${API}/api/voice/call`, {
        method: 'POST', headers,
        body: JSON.stringify({ candidate_id: candidateId || null, phone_number: dialNumber })
      });
      if (logRes.ok) {
        const logData = await logRes.json();
        logId = String(logData.call.id);
        setCallLogId(logData.call.id);
      }

      const call = await deviceRef.current.connect({
        params: { To: dialNumber, CallerId: callerId, Record: recording ? 'true' : 'false', CallLogId: logId }
      });

      setActiveCall(call);

      call.on('accept', () => {
        setStatus('connected');
        startTimeRef.current = Date.now();
        timerRef.current = setInterval(() => {
          setCallDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }, 1000);
      });

      call.on('disconnect', () => {
        if (timerRef.current) clearInterval(timerRef.current);
        setStatus('ended');
        setActiveCall(null);
      });

      call.on('cancel', () => { setStatus('ended'); setActiveCall(null); });
      call.on('error', (err) => { setError(err.message || 'Call failed'); setStatus('ended'); });
      call.on('ringing', () => setStatus('calling'));

    } catch (err) {
      console.error('[Voice] Call failed:', err);
      setError(err.message || 'Call failed');
      setStatus('ready');
    }
  }, [dialNumber, candidateId, callerId]);

  const hangUp = useCallback(() => {
    if (activeCall) activeCall.disconnect();
    else if (deviceRef.current) deviceRef.current.disconnectAll();
    if (timerRef.current) clearInterval(timerRef.current);
    setStatus('ended');
  }, [activeCall]);

  const toggleMute = useCallback(() => {
    if (activeCall) { const n = !muted; activeCall.mute(n); setMuted(n); }
  }, [activeCall, muted]);

  const skipAndClose = async () => {
    if (callLogId) {
      try {
        // If recording is on, don't create note now — transcription handler will create combined note
        await fetch(`${API}/api/voice/call/${callLogId}`, {
          method: 'PUT', headers,
          body: JSON.stringify({ candidate_id: candidateId, duration_seconds: callDuration, status: 'completed', log_call: !recording })
        });
      } catch (e) {}
    }
    onCallComplete?.({ duration: callDuration });
    onClose?.();
  };

  const saveNotes = async () => {
    if (!callLogId || !callNotes.trim()) return;
    setSavingNotes(true);
    try {
      // If recording is on, save notes to call log but don't create candidate note yet — transcription handler will combine
      await fetch(`${API}/api/voice/call/${callLogId}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ notes: callNotes, candidate_id: candidateId, duration_seconds: callDuration, log_call: !recording })
      });
      onCallComplete?.({ duration: callDuration, notes: callNotes });
      onClose?.();
    } catch (e) { console.error(e); }
    finally { setSavingNotes(false); }
  };

  const fmt = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;
  const fmtPhone = (p) => {
    if (!p) return '';
    const c = p.replace(/[^+\d]/g,'');
    return (c.length===12 && c.startsWith('+1')) ? `+1 (${c.slice(2,5)}) ${c.slice(5,8)}-${c.slice(8)}` : p;
  };

  return (
    <div className="bg-white rounded-xl shadow-2xl border w-80 overflow-hidden">
      <div className={`px-4 py-3 flex items-center justify-between ${status==='connected'?'bg-green-600':status==='calling'?'bg-blue-600':'bg-gray-800'} text-white`}>
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
          <span className="text-sm font-medium">
            {status==='initializing' && 'Connecting...'}
            {status==='ready' && 'Ready to Call'}
            {status==='calling' && 'Ringing...'}
            {status==='connected' && fmt(callDuration)}
            {status==='connected' && recording && ' 🔴'}
            {status==='ended' && `Call Ended · ${fmt(callDuration)}`}
            {status==='error' && 'Error'}
          </span>
        </div>
        {onClose && status!=='connected' && status!=='calling' && (
          <button onClick={onClose} className="text-white/70 hover:text-white"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
        )}
      </div>

      <div className="p-4">
        {error && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">{error}</div>}

        {candidateName && (
          <div className="text-center mb-3">
            <div className="text-sm font-semibold text-gray-900">{candidateName}</div>
            {dialNumber && <div className="text-xs text-gray-400 font-mono">{fmtPhone(dialNumber)}</div>}
          </div>
        )}

        {!dialNumber && (status==='ready' || status==='error') && (
          <div className="mb-3">
            <input type="tel" value={dialNumber} onChange={(e) => setDialNumber(e.target.value)}
              placeholder="+1 (555) 123-4567" className="w-full px-3 py-2 text-center text-lg font-mono border rounded-lg focus:ring-2 focus:ring-blue-500"
              onKeyDown={(e) => e.key==='Enter' && makeCall()} />
            {candidateName && <p className="text-[10px] text-gray-400 text-center mt-1">No phone number on file — enter one to call</p>}
          </div>
        )}

        <div className="flex items-center justify-center gap-3">
          {(status==='ready' || status==='error') && (
            <button onClick={makeCall} disabled={!dialNumber}
              className="w-14 h-14 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center shadow-lg disabled:opacity-50 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
            </button>
          )}
          {status==='calling' && (
            <button onClick={hangUp} className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg animate-pulse">
              <svg className="w-6 h-6 rotate-[135deg]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
            </button>
          )}
          {status==='connected' && (
            <>
              <button onClick={toggleMute} className={`w-11 h-11 rounded-full flex items-center justify-center ${muted?'bg-red-100 text-red-600':'bg-gray-100 text-gray-600'} hover:bg-gray-200`}>
                {muted ? <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" /></svg>
                       : <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" /></svg>}
              </button>
              <button onClick={hangUp} className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 rotate-[135deg]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
              </button>
            </>
          )}
        </div>

        {status==='ended' && (
          <div className="mt-4 space-y-2">
            {recording && (
              <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg text-xs text-indigo-700">
                <svg className="w-3.5 h-3.5 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                AI transcription will be added to notes automatically
              </div>
            )}
            <textarea value={callNotes} onChange={(e) => setCallNotes(e.target.value)}
              placeholder="Add call notes..." className="w-full px-3 py-2 text-sm border rounded-lg resize-none focus:ring-2 focus:ring-blue-500" rows={3} autoFocus />
            <div className="flex items-center gap-2">
              <button onClick={saveNotes} disabled={savingNotes || !callNotes.trim()}
                className="flex-1 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
                {savingNotes ? 'Saving...' : 'Save Notes & Close'}
              </button>
              <button onClick={skipAndClose}
                className="px-3 py-2 text-sm text-gray-500 border rounded-lg hover:bg-gray-50">Skip</button>
            </div>
          </div>
        )}

        {callerId && status==='ready' && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-center">
              <button onClick={() => setRecording(!recording)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium transition-colors ${recording ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-gray-50 text-gray-400 border border-gray-200'}`}>
                <span className={`w-2 h-2 rounded-full ${recording ? 'bg-red-500 animate-pulse' : 'bg-gray-300'}`}></span>
                {recording ? 'Record & Transcribe' : 'Recording off'}
              </button>
            </div>
            <div className="text-center text-[10px] text-gray-400">Calling from {fmtPhone(callerId)}</div>
          </div>
        )}
        {status==='initializing' && <div className="flex items-center justify-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div></div>}
      </div>
    </div>
  );
};

export default CallWidget;
